import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Star, Flame, Sparkles, BookOpen, Trophy, ArrowRight } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { dataLayer } from '../../data/dataLayer';
import { Card } from '../ui/Card';
import { ErrorPatternsSection } from './ErrorPatternsSection';
import { MilestonesSection } from './MilestonesSection';
import { PersonalRecordsSection } from './PersonalRecordsSection';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { WorldZone } from '../shared/WorldZone';
import { Button } from '../ui/Button';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import type { AssessmentReport } from '../../assessment';
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
  onNavigate: (view: string) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport,
  onNavigate
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const activeAccountId = accounts?.activeAccountId;
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  useEffect(() => {
    if (activeAccountId) {
      dataLayer.getDjayLongPressDiscovered(activeAccountId).then(setHasDiscoveredLongPress);
    }
  }, [activeAccountId]);
  // Calculate streak
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  // Calculate personal records
  const records = useMemo(() => {
    let fastestTime = Infinity;
    let totalCorrect = 0;
    let totalAttempts = 0;
    if (state.questionStates) {
      Object.values(state.questionStates).forEach(qs => {
        qs.history?.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            totalCorrect++;
            // Filter out impossibly fast clicks (< 500ms)
            if (attempt.responseTimeMs && attempt.responseTimeMs > 500) {
              fastestTime = Math.min(fastestTime, attempt.responseTimeMs);
            }
          }
        });
      });
    }
    return {
      fastestTime: fastestTime === Infinity ? null : fastestTime,
      accuracy: totalAttempts > 0 ? Math.round(totalCorrect / totalAttempts * 100) : 0,
      longestStreak: state.dailyStreak?.longestWeekStreak || 0,
      perfectSessions: state.dailyStreak?.totalPerfectSessions || 0,
      dailyGoals: state.dailyStreak?.totalDailyGoalsReached || 0
    };
  }, [state.questionStates, state.dailyStreak]);
  // Get weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Calculate XP progress
  const xpInCurrentLevel = state.stats.xp % 1000;
  const levelProgress = Math.min(100, xpInCurrentLevel / 1000 * 100);
  return <PageContainer className="pb-20" data-id="element-1865">
      {/* Hero Header with Djay */}
      <motion.div variants={itemVariants} data-id="element-1866">
        <PageHero title="Mijn Voortgang" subtitle="Bekijk hoe je groeit per vak en houd je records bij! 📊" gradientClasses="from-indigo-500 via-violet-500 to-purple-500" djayEmotion="celebrating" djayVariant={selectedVariant} icon={TrendingUp} iconBgClass="bg-indigo-400" floatingEmojis={['📊', '⭐']} data-id="element-1867">
          <HeroStatPill icon={Star} data-id="element-1868">Level {state.stats.level}</HeroStatPill>
          <HeroStatPill icon={Sparkles} data-id="element-1869">
            {state.stats.xp.toLocaleString('nl-NL')} XP
          </HeroStatPill>
          <HeroStatPill icon={Flame} data-id="element-1870">
            {currentStreak} dagen deze week
          </HeroStatPill>
        </PageHero>
      </motion.div>

      {/* Subject Mastery Overview */}
      <motion.div variants={itemVariants} data-id="element-1871">
        <WorldZone icon={BookOpen} title="Vakken Overzicht" accentColor="bg-blue-100 text-blue-600" data-id="element-1872">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-1873">
            {MODULES.map(module => {
            const mastery = getModuleMasteryForGroup(module.id);
            const answeredCount = getAnsweredQuestions(module.id).length;
            const isMastered = mastery >= 85;
            // Extract base color (e.g., 'blue' from 'text-blue-500')
            const colorBase = module.color.split('-')[1] || 'slate';
            const bgClass = `bg-${colorBase}-100`;
            const borderClass = `border-${colorBase}-200`;
            const fillClass = `bg-${colorBase}-500`;
            return <Card key={module.id} className={`p-4 border-2 ${borderClass} bg-white rounded-2xl flex flex-col hover:shadow-md transition-shadow`} data-id="element-1874">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1875">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm ${bgClass}`} data-id="element-1876">
                      {module.icon === 'Ruler' ? '📏' : module.icon === 'Calculator' ? '🔢' : module.icon === 'Plus' ? '➕' : module.icon === 'Clock' ? '⏰' : module.icon === 'Wallet' ? '💶' : module.icon === 'PenTool' ? '✍️' : module.icon === 'BookOpen' ? '📖' : module.icon === 'MessageSquare' ? '💬' : module.icon === 'Search' ? '🔍' : module.icon === 'Globe' ? '🌍' : '📚'}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1877">
                      <h4 className="font-bold text-slate-800 text-sm truncate" data-id="element-1878">
                        {module.title}
                      </h4>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-1879">
                        {answeredCount} vragen
                      </p>
                    </div>
                  </div>

                  <div className="mt-auto" data-id="element-1880">
                    <div className="flex justify-between items-end mb-1.5" data-id="element-1881">
                      <span className="text-2xl font-black text-slate-800 leading-none" data-id="element-1882">
                        {mastery}%
                      </span>
                      {isMastered && <span className="text-xs font-bold text-amber-500 flex items-center gap-1" data-id="element-1883">
                          <Star className="w-3 h-3 fill-current" data-id="element-1884" />
                          Klaar!
                        </span>}
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1885">
                      <div className={`h-full ${fillClass} rounded-full transition-all duration-1000 ease-out`} style={{
                    width: `${mastery}%`
                  }} data-id="element-1886" />
                    </div>
                  </div>
                </Card>;
          })}
          </div>
        </WorldZone>
      </motion.div>

      {/* XP & Level Card */}
      <motion.div variants={itemVariants} data-id="element-1887">
        <Card className="p-5 sm:p-6 bg-white border-2 border-indigo-100 shadow-sm rounded-[2rem]" data-id="element-1888">
          <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-1889">
            <div className="flex-1 w-full" data-id="element-1890">
              <div className="flex items-center gap-3 mb-4" data-id="element-1891">
                <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center" data-id="element-1892">
                  <Star className="w-6 h-6 fill-current" data-id="element-1893" />
                </div>
                <div data-id="element-1894">
                  <h3 className="text-xl font-black text-slate-800" data-id="element-1895">
                    Level {state.stats.level}
                  </h3>
                  <p className="text-sm font-bold text-indigo-600 flex items-center gap-1" data-id="element-1896">
                    <Sparkles className="w-4 h-4" data-id="element-1897" />
                    {state.stats.xp.toLocaleString('nl-NL')} XP Totaal
                  </p>
                </div>
              </div>

              <div className="space-y-2" data-id="element-1898">
                <div className="flex justify-between text-sm font-bold" data-id="element-1899">
                  <span className="text-slate-500" data-id="element-1900">
                    Voortgang naar Level {state.stats.level + 1}
                  </span>
                  <span className="text-indigo-600" data-id="element-1901">
                    {xpInCurrentLevel} / 1000 XP
                  </span>
                </div>
                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1902">
                  <div className="h-full bg-indigo-500 rounded-full transition-all duration-1000 ease-out" style={{
                  width: `${levelProgress}%`
                }} data-id="element-1903" />
                </div>
              </div>
            </div>

            <div className="w-full md:w-64 bg-slate-50 rounded-2xl p-4 border border-slate-100" data-id="element-1904">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 text-center" data-id="element-1905">
                Activiteit Deze Week
              </h4>
              <WeeklyActivityDots weeklyActivity={weeklyActivity} color="indigo" data-id="element-1906" />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Error Patterns Section (Slimme Leertips) */}
      <motion.div variants={itemVariants} data-id="element-1907">
        <ErrorPatternsSection state={state} selectedGroup={selectedGroup} data-id="element-1908" />
      </motion.div>

      {/* Milestones Section */}
      <motion.div variants={itemVariants} data-id="element-1909">
        <MilestonesSection state={state} selectedGroup={selectedGroup} data-id="element-1910" />
      </motion.div>

      {/* Persoonlijke Records */}
      <motion.div variants={itemVariants} data-id="element-1911">
        <PersonalRecordsSection fastestTime={records.fastestTime} accuracy={records.accuracy} longestStreak={records.longestStreak} perfectSessions={records.perfectSessions} dailyGoals={records.dailyGoals} data-id="element-1912" />
      </motion.div>

      {/* Collection Link */}
      <motion.div variants={itemVariants} data-id="element-1913">
        <Card className="p-4 sm:p-5 bg-amber-50 border-2 border-amber-200 shadow-sm rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 hover:shadow-md transition-shadow" data-id="element-1914">
          <div className="flex items-center gap-3" data-id="element-1915">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-2xl shadow-sm" data-id="element-1916">
              🏆
            </div>
            <div data-id="element-1917">
              <h3 className="font-bold text-slate-800" data-id="element-1918">Mijn Collectie</h3>
              <p className="text-sm text-slate-600" data-id="element-1919">
                Bekijk je behaalde prestaties en Djay-varianten
              </p>
            </div>
          </div>
          <Button onClick={() => onNavigate('collection')} className="w-full sm:w-auto bg-amber-100 text-amber-700 hover:bg-amber-200 border-none" data-id="element-1920">
            Bekijk collectie <ArrowRight className="w-4 h-4 ml-2" data-id="element-1921" />
          </Button>
        </Card>
      </motion.div>
    </PageContainer>;
}